import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../components/Modal";

// Module-level bridge so any module can trigger the in-app dialog without a hook.
let _impl = null;

export function confirmDialog(opts = {}) {
  if (_impl) return _impl.confirm(opts);
  // Provider should always be mounted (App.js). Warn instead of silently using a native popup.
  console.warn("ConfirmProvider not mounted — falling back to native confirm");
  return Promise.resolve(window.confirm(opts.message || opts.title || "Are you sure?"));
}

export function promptDialog(opts = {}) {
  if (_impl) return _impl.prompt(opts);
  console.warn("ConfirmProvider not mounted — falling back to native prompt");
  return Promise.resolve(window.prompt(opts.message || opts.title || "", opts.defaultValue || ""));
}

// Three-way unsaved-changes dialog. Resolves to "save" | "discard" | "cancel".
export function saveDiscardDialog(opts = {}) {
  if (_impl) return _impl.choice(opts);
  const r = window.confirm(`${opts.message || "You have unsaved changes."}\n\nOK = Save, Cancel = Discard`);
  return Promise.resolve(r ? "save" : "discard");
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const settle = useCallback((result) => {
    if (resolver.current) { resolver.current(result); resolver.current = null; }
    setState(null);
  }, []);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({
      mode: "confirm",
      eyebrow: opts.eyebrow || "Confirm",
      title: opts.title || "Are you sure?",
      message: opts.message || "",
      confirmLabel: opts.confirmLabel || "Confirm",
      cancelLabel: opts.cancelLabel || "Cancel",
      destructive: opts.destructive !== false,
      requireText: opts.requireText || null,
      placeholder: opts.placeholder || "",
      value: "",
    });
  }), []);

  const prompt = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({
      mode: "prompt",
      eyebrow: opts.eyebrow || "Input",
      title: opts.title || "Enter a value",
      message: opts.message || "",
      confirmLabel: opts.confirmLabel || "Save",
      cancelLabel: opts.cancelLabel || "Cancel",
      placeholder: opts.placeholder || "",
      value: opts.defaultValue || "",
      destructive: false,
    });
  }), []);

  const choice = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({
      mode: "savediscard",
      eyebrow: opts.eyebrow || "Unsaved changes",
      title: opts.title || "Save your changes?",
      message: opts.message || "",
      saveLabel: opts.saveLabel || "Save",
      discardLabel: opts.discardLabel || "Discard",
      cancelLabel: opts.cancelLabel || "Keep editing",
    });
  }), []);

  useEffect(() => {
    _impl = { confirm, prompt, choice };
    return () => { if (_impl && _impl.confirm === confirm) _impl = null; };
  }, [confirm, prompt, choice]);

  const requireText = state?.mode === "confirm" ? state?.requireText : null;
  const textMatched = !requireText || (state?.value || "").trim().toLowerCase() === requireText.trim().toLowerCase();
  const accept = () => {
    if (requireText && !textMatched) return;
    settle(state?.mode === "prompt" ? (state.value ?? "") : true);
  };
  const cancel = () => settle(state?.mode === "prompt" ? null : state?.mode === "savediscard" ? "cancel" : false);

  return (
    <>
      {children}
      <Modal
        open={!!state}
        onClose={cancel}
        size="sm"
        eyebrow={state?.eyebrow}
        title={state?.title || ""}
        testId="confirm-dialog"
        footer={state && (
          state.mode === "savediscard" ? (
            <>
              <button onClick={cancel} className="btn-ghost" data-testid="confirm-keep-editing">{state.cancelLabel}</button>
              <button onClick={() => settle("discard")} className="px-4 py-2 text-sm border border-border hover:bg-accent text-muted-foreground transition-colors" style={{ borderRadius: 6 }} data-testid="confirm-discard">{state.discardLabel}</button>
              <button onClick={() => settle("save")} className="btn-school" data-testid="confirm-save">{state.saveLabel}</button>
            </>
          ) : (
            <>
              <button onClick={cancel} className="btn-ghost" data-testid="confirm-cancel">{state.cancelLabel}</button>
              <button
                onClick={accept}
                disabled={requireText && !textMatched}
                className={state.destructive ? "px-4 py-2 text-sm bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-500" : "btn-school disabled:opacity-40 disabled:cursor-not-allowed"}
                style={state.destructive ? { borderRadius: 6 } : undefined}
                data-testid="confirm-accept"
              >
                {state.confirmLabel}
              </button>
            </>
          )
        )}
      >
        {state?.message && <p className="text-sm text-muted-foreground whitespace-pre-line" data-testid="confirm-message">{state.message}</p>}
        {requireText && (
          <input
            autoFocus
            className="input-brutal w-full mt-3"
            placeholder={state.placeholder || requireText}
            value={state.value}
            onChange={(e) => setState((s) => (s ? { ...s, value: e.target.value } : s))}
            onKeyDown={(e) => { if (e.key === "Enter" && textMatched) accept(); }}
            data-testid="confirm-require-input"
          />
        )}
        {state?.mode === "prompt" && (
          <input
            autoFocus
            className="input-brutal w-full mt-3"
            placeholder={state.placeholder}
            defaultValue={state.value}
            onChange={(e) => setState((s) => (s ? { ...s, value: e.target.value } : s))}
            onKeyDown={(e) => { if (e.key === "Enter") accept(); }}
            data-testid="confirm-input"
          />
        )}
      </Modal>
    </>
  );
}
